import { A, D, flow, G, pipe, S } from "@mobily/ts-belt";
import { format } from "date-fns";
import PropTypes from "prop-types";
import * as React from "react";
import { fetcher } from "../../../hooks";
import {
  Button,
  Card,
  CopyButton,
  Dialog,
  Skeleton,
  TextInput,
  Tooltip,
  Typography,
} from "../../../themeV2";
import UserSearch from "../../UserSearch";
import {
  useAffiliateLinks,
  useChanges,
} from "../../Dashboard/InfluencerModal/AffiliateLinks/service";
import {
  isValidPercentage,
  parsePercentage,
} from "../../Dashboard/InfluencerModal/AffiliateLinks/utils";
import styles from "./AffiliateLinksContent.module.css";

const formatDate = (str) => {
  try {
    return format(new Date(str), "MM/dd/yyyy");
  } catch {
    return "N/A";
  }
};

const parseDiff = (diff) => {
  if (!G.isObject(diff) || D.isEmpty(diff)) return "";

  return pipe(
    diff,
    D.selectKeys(["code", "percentage"]),
    D.toPairs,
    A.map(([key, data]) => {
      if (data.new && data.old) {
        return `${key} to ${data.new} from ${data.old}`;
      }
      return null;
    }),
    A.filter(Boolean),
    A.join(", "),
  );
};

const createDraftLink = (affiliateLink) =>
  D.set(
    affiliateLink,
    "percentage",
    `${Math.round(affiliateLink.percentage * 10000) / 100}%`,
  );

/* ─── Individual link card ─── */

function AffiliateLinkCard({
  affiliateLink,
  influencerRoute,
  refresh,
  setError,
}) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [draftLink, setDraftLink] = React.useState(
    createDraftLink(affiliateLink),
  );

  const link = React.useMemo(() => {
    const params = new URLSearchParams({
      source: "aff",
      medium: draftLink.code,
    });
    return `streamily.com/${influencerRoute}?${params}`;
  }, [influencerRoute, draftLink.code]);

  const handleCancel = () => {
    setIsEditing(false);
    setDraftLink(createDraftLink(affiliateLink));
    setError("");
  };

  const handleSave = () => {
    const updates = pipe(
      draftLink,
      D.selectKeys(["code", "percentage", "influencerId", "userId"]),
      D.update("percentage", parsePercentage),
      D.update("code", flow(S.toLowerCase, S.trim)),
    );

    if (
      updates.code === affiliateLink.code &&
      updates.percentage === affiliateLink.percentage
    ) {
      setIsEditing(false);
      setDraftLink(createDraftLink(affiliateLink));
      return;
    }

    if (!isValidPercentage(updates.percentage)) {
      setError("Invalid percentage");
      return;
    }

    if (S.isEmpty(updates.code)) {
      setError("Code cannot be empty");
      return;
    }

    if (updates.code.length > 100) {
      setError("Code cannot be longer than 100 characters");
      return;
    }

    setIsLoading(true);

    fetcher(`/api/affiliateLink/${affiliateLink.affiliateLinkId}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    })
      .then(({ affiliateLink }) => {
        setDraftLink(createDraftLink(affiliateLink));
        setIsEditing(false);
      })
      .catch((err) => {
        setError(err?.json?.message ?? "Something went wrong");
      })
      .finally(() => {
        setIsLoading(false);
        return refresh();
      });
  };

  const handleDelete = () => {
    setIsLoading(true);

    window
      .fetch(`/api/affiliateLink/${affiliateLink.affiliateLinkId}`, {
        method: "DELETE",
      })
      .then((res) => {
        if (!res.ok) throw new Error("Something went wrong");
        return refresh();
      })
      .catch((err) => {
        setError(err?.json?.errors[0]?.message ?? "Something went wrong");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const isInactive = !affiliateLink.active;

  const cardClassName = [
    styles.card,
    isEditing && styles.cardEditing,
    isInactive && styles.cardInactive,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Card.Root variant="outlined" className={cardClassName}>
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <Typography variant="label-lg">
            #{affiliateLink.affiliateLinkId}
          </Typography>
          {isInactive && (
            <Typography variant="label-md" color="variant">
              Inactive
            </Typography>
          )}
        </div>
        <Typography variant="body-md" color="variant">
          {affiliateLink.user?.email ?? "No affiliate email"}
        </Typography>
      </div>

      <div className={styles.cardFields}>
        <TextInput
          label="Code"
          disabled={isInactive || isLoading}
          readOnly={!isEditing}
          value={draftLink.code}
          onChange={(evt) => {
            setError("");
            const code = pipe(evt.target.value, S.toLowerCase, S.trim);
            setDraftLink((prev) => D.set(prev, "code", code));
          }}
        />
        <TextInput
          label="Percentage"
          disabled={isInactive || isLoading}
          inputMode="numeric"
          readOnly={!isEditing}
          value={draftLink.percentage}
          onChange={(evt) => {
            setError("");
            const percentage = pipe(evt.target.value, S.trim);
            setDraftLink((prev) => D.set(prev, "percentage", percentage));
          }}
        />
      </div>

      <div className={styles.cardDetail}>
        <Typography variant="label-md" color="variant">
          Affiliate URL
        </Typography>
        <div className={styles.cardUrl}>
          <Typography variant="body-md">{link}</Typography>
          <Tooltip.Root>
            <Tooltip.Trigger
              render={
                <CopyButton
                  text={link}
                  label="Copy affiliate URL"
                  disabled={isInactive}
                />
              }
            />
            <Tooltip.Popup>Copy URL</Tooltip.Popup>
          </Tooltip.Root>
        </div>
      </div>

      <div className={styles.actions}>
        {isEditing ? (
          <>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={isLoading}
              loading={isLoading}
            >
              Save
            </Button>
            <Button
              size="sm"
              variant="outlined"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button
              size="sm"
              variant="tonal"
              onClick={() => setIsEditing(true)}
              disabled={isInactive || isLoading}
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="outlined"
              onClick={handleDelete}
              disabled={isInactive || isLoading}
            >
              Delete
            </Button>
          </>
        )}
      </div>
    </Card.Root>
  );
}

AffiliateLinkCard.propTypes = {
  affiliateLink: PropTypes.shape({
    affiliateLinkId: PropTypes.number.isRequired,
    influencerId: PropTypes.number.isRequired,
    userId: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
    code: PropTypes.string.isRequired,
    percentage: PropTypes.number.isRequired,
    active: PropTypes.bool.isRequired,
    created: PropTypes.string.isRequired,
    updated: PropTypes.string.isRequired,
    user: PropTypes.shape({ email: PropTypes.string }),
  }).isRequired,
  influencerRoute: PropTypes.string.isRequired,
  refresh: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
};

/* ─── Change Log dialog ─── */

function ChangeLogDialog({ influencerId }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const { changes } = useChanges(influencerId);

  return (
    <>
      <Button variant="outlined" onClick={() => setIsOpen(true)}>
        Change log
      </Button>

      <Dialog.Root
        open={isOpen}
        onOpenChange={(open) => !open && setIsOpen(false)}
      >
        <Dialog.Popup size="md">
          <Dialog.Title>Change log</Dialog.Title>
          <Dialog.Close />

          <div className={styles.changeLogList}>
            {changes.length === 0 && (
              <Typography variant="body-md" color="variant">
                No changes recorded.
              </Typography>
            )}
            {changes.map((change) => (
              <div key={change.changeId} className={styles.changeLogEntry}>
                <Typography variant="body-md">
                  User: {change.userId ?? "N/A"}
                </Typography>
                <Typography variant="body-lg">
                  {change.description} {parseDiff(change.diff)}
                </Typography>
                <Typography variant="body-md" color="variant">
                  {formatDate(change.created)}
                </Typography>
              </div>
            ))}
          </div>
        </Dialog.Popup>
      </Dialog.Root>
    </>
  );
}

ChangeLogDialog.propTypes = {
  influencerId: PropTypes.number.isRequired,
};

/* ─── New Link dialog ─── */

function NewLinkDialog({ influencerId, refresh }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [user, setUser] = React.useState(null);

  const handleSubmit = (evt) => {
    evt.preventDefault();

    const formData = pipe(
      Object.fromEntries(new FormData(evt.target)),
      D.update("percentage", parsePercentage),
      D.update("code", flow(S.toLowerCase, S.trim)),
      D.set("influencerId", influencerId),
      D.set("userId", user?.userId ?? null),
    );

    if (!isValidPercentage(formData.percentage)) {
      setError("Invalid percentage");
      return;
    }

    setIsLoading(true);

    fetcher("/api/affiliateLink", {
      method: "POST",
      body: JSON.stringify(formData),
    })
      .then(() => {
        setIsLoading(false);
        setIsOpen(false);
        setUser(null);
        setError("");
        refresh();
      })
      .catch((err) => {
        setIsLoading(false);
        setError(err?.json?.message ?? "Something went wrong");
      });
  };

  return (
    <>
      <Button onClick={() => setIsOpen(true)}>New link</Button>

      <Dialog.Root
        open={isOpen}
        onOpenChange={(open) => !open && setIsOpen(false)}
      >
        <Dialog.Popup size="sm">
          <Dialog.Title>New link</Dialog.Title>
          <Dialog.Close />

          <form
            onSubmit={handleSubmit}
            className={styles.newLinkForm}
            onChange={() => setError("")}
          >
            <div className={styles.newLinkRow}>
              <TextInput
                label="Code"
                required
                disabled={isLoading}
                name="code"
              />
              <TextInput
                label="Percentage"
                required
                disabled={isLoading}
                inputMode="numeric"
                name="percentage"
              />
            </div>

            <UserSearch
              onAddUser={(u) => setUser(u)}
              onRemoveUser={() => setUser(null)}
              user={user}
            />

            {error && (
              <Typography variant="body-md" color="error" role="alert">
                {error}
              </Typography>
            )}

            <Button
              type="submit"
              disabled={isLoading}
              loading={isLoading}
              style={{ alignSelf: "stretch" }}
            >
              Save
            </Button>
          </form>
        </Dialog.Popup>
      </Dialog.Root>
    </>
  );
}

NewLinkDialog.propTypes = {
  influencerId: PropTypes.number.isRequired,
  refresh: PropTypes.func.isRequired,
};

/* ─── Main content component ─── */

const AffiliateLinksContent = React.memo(function AffiliateLinksContent({
  influencerId,
  influencerRoute,
}) {
  const [error, setError] = React.useState("");
  const { affiliateLinks, isLoading, isError, refresh } =
    useAffiliateLinks(influencerId);

  return (
    <div>
      <Typography
        variant="body-lg"
        color="variant"
        className={styles.description}
      >
        Any sales made through these links will have a percentage deducted from
        the Talent&apos;s eligible payout.
      </Typography>

      {isLoading && (
        <div className={styles.center}>
          <Skeleton variant="rect" width="100%" height={118} />
        </div>
      )}

      {isError && (
        <Typography variant="body-md" color="error" role="alert">
          There was an error loading affiliate links.
        </Typography>
      )}

      {!isLoading && affiliateLinks.length === 0 && !isError && (
        <Typography variant="body-lg" color="variant">
          No affiliate links.
        </Typography>
      )}

      {!isLoading && affiliateLinks.length > 0 && (
        <div className={styles.list}>
          {affiliateLinks.map((link) => (
            <AffiliateLinkCard
              key={link.affiliateLinkId}
              affiliateLink={link}
              influencerRoute={influencerRoute}
              refresh={refresh}
              setError={setError}
            />
          ))}
        </div>
      )}

      {error && (
        <Typography
          variant="body-md"
          color="error"
          role="alert"
          style={{ marginBlockStart: "var(--space-2)" }}
        >
          {error}
        </Typography>
      )}

      <div className={styles.footer}>
        <ChangeLogDialog influencerId={influencerId} />
        <NewLinkDialog influencerId={influencerId} refresh={refresh} />
      </div>
    </div>
  );
});

AffiliateLinksContent.displayName = "AffiliateLinksContent";

AffiliateLinksContent.propTypes = {
  influencerId: PropTypes.number.isRequired,
  influencerRoute: PropTypes.string.isRequired,
};

export default AffiliateLinksContent;
